interface MarketplaceSVGProps {
  color?: string;
}

const MarketplaceSVG: React.FC<MarketplaceSVGProps> = ({
  color = "#4F46E5",
}) => {
  return (
    <svg
      viewBox="0 0 400 300"
      className={`w-full h-full transform transition-transform duration-300 rounded-lg`}
    >
      <defs>
        <linearGradient id="grid-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.1" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
        <filter id="marketplace-glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <path
        d="M0 0h400v300H0z"
        fill="url(#grid-gradient)"
        className="transition-opacity duration-300"
      />

      {/* Blueprint Grid */}
      <g stroke={color} strokeWidth="0.5" opacity="0.1">
        {[...Array(20)].map((_, i) => (
          <line key={`v${i}`} x1={i * 20} y1="0" x2={i * 20} y2="300" />
        ))}
        {[...Array(15)].map((_, i) => (
          <line key={`h${i}`} x1="0" y1={i * 20} x2="400" y2={i * 20} />
        ))}
      </g>

      <style>
        {`
          @keyframes orbit {
            from { transform: rotate(0deg) translateX(2px) rotate(0deg); }
            to   { transform: rotate(360deg) translateX(2px) rotate(-360deg); }
          }
          @keyframes pulse-subtle {
            0% { transform: scale(1); opacity: 0.6; }
            50% { transform: scale(1.02); opacity: 0.7; }
            100% { transform: scale(1); opacity: 0.6; }
          }
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-2px); }
            100% { transform: translateY(0px); }
          }
          .orbit { animation: orbit 30s linear infinite; }
          .pulse-subtle { animation: pulse-subtle 8s ease-in-out infinite; }
          .float { animation: float 10s ease-in-out infinite; }
        `}
      </style>

      <g className="transform transition-transform duration-500 float">
        {/* Central Hub with pulse animation */}
        <g filter="url(#marketplace-glow)" className="pulse-subtle">
          <circle
            cx="200"
            cy="150"
            r="50"
            fill={color}
            opacity="0.1"
            className="transition-colors duration-500"
          />
          <circle
            cx="200"
            cy="150"
            r="40"
            fill={color}
            opacity="0.12"
            className="transition-colors duration-500"
          />
          <circle
            cx="200"
            cy="150"
            r="30"
            fill={color}
            opacity="0.15"
            className="transition-colors duration-500"
          />

          {/* Hub Icons */}
          {[0, 72, 144, 216, 288].map((angle, i) => {
            const x = 200 + Math.cos((angle * Math.PI) / 180) * 25;
            const y = 150 + Math.sin((angle * Math.PI) / 180) * 25;
            return (
              <g key={i}>
                <circle
                  cx={x}
                  cy={y}
                  r="3"
                  fill={color}
                  opacity="0.4"
                  className="transition-colors duration-500"
                />
              </g>
            );
          })}
        </g>

        {/* Orbiting Nodes */}
        {[0, 72, 144, 216, 288].map((angle, i) => {
          const x = 200 + Math.cos((angle * Math.PI) / 180) * 80;
          const y = 150 + Math.sin((angle * Math.PI) / 180) * 80;
          return (
            <g
              key={i}
              className="orbit"
              style={{ animationDelay: `${-i * 3}s` }}
            >
              <circle
                cx={x}
                cy={y}
                r="20"
                fill={color}
                opacity="0.08"
                className="transition-colors duration-500"
              />
              <circle
                cx={x}
                cy={y}
                r="15"
                fill={color}
                opacity="0.1"
                className="transition-colors duration-500"
              />
              <circle
                cx={x}
                cy={y}
                r="10"
                fill={color}
                opacity="0.15"
                className="transition-colors duration-500"
              />

              {/* Connection Lines */}
              <line
                x1="200"
                y1="150"
                x2={x}
                y2={y}
                stroke={color}
                strokeWidth="1.5"
                opacity="0.15"
                strokeDasharray="4,4"
                className="transition-colors duration-500"
              />

              {/* Data Flow Particles */}
              <circle
                cx={200 + Math.cos((angle * Math.PI) / 180) * (40 + i * 8)}
                cy={150 + Math.sin((angle * Math.PI) / 180) * (40 + i * 8)}
                r="2.5"
                fill={color}
                opacity="0.4"
                className="transition-colors duration-500"
                style={{ animationDelay: `${i * 1.5}s` }}
              />
            </g>
          );
        })}

        {/* Decorative arcs */}
        <g>
          {[0, 120, 240].map((angle, i) => (
            <path
              key={i}
              d={`M${200 + Math.cos((angle * Math.PI) / 180) * 100} ${
                150 + Math.sin((angle * Math.PI) / 180) * 100
              } A 100 100 0 0 1 ${
                200 + Math.cos(((angle + 60) * Math.PI) / 180) * 100
              } ${150 + Math.sin(((angle + 60) * Math.PI) / 180) * 100}`}
              fill="none"
              stroke={color}
              strokeWidth="1"
              opacity="0.1"
              strokeDasharray="4,4"
              className="transition-colors duration-500"
            />
          ))}
        </g>
      </g>
    </svg>
  );
};

export default MarketplaceSVG;
